import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { USDollar, htmlDecode, dateFormat, noop } from "./util.js";

export function Grant(
	{
		grant_id,
		grant_amount,
		grantee_name,
		description,
		approval_date,
		link,
		types,
		onClick,
		...props
	}
) {
	let label = '';

	if ( types && types.length ) {
		for ( let type of types ) {
			if ( 'regular' !== type.type_slug ) {
				label = type.type_name;
				break;
			}
		}
	}

	return (
		<article className="grants-db__grant" data-id={ grant_id } {...props}>
			<div className="grants-db__grant-amount">{ USDollar.format(grant_amount) }</div>
			<div className="grants-db__grant-result">
				<strong>
					<a href={ link } onClick={ onClick } className="link-override h6">
						{ htmlDecode( grantee_name ) }
					</a>
					{ label && (
						<>
							{ ' ' }
							<span className="grants-db__grant-label">{ label }</span>
						</>
					) }
				</strong>
				<br />
				{ htmlDecode( description ) }
			</div>
			<div className="grants-db__grant-date">{ dateFormat( approval_date ) }</div>
		</article>
	);
}

export function SortLink({
	orderby,
	order,
	active,
	params = new URLSearchParams( location.search ),
	onClick = noop,
	children,
	...props
}) {
	params.set( 'orderby', orderby );
	params.set( 'order', order );

	return (
		<span className="grants-db__sort-link">
			<a
				href={ `${location.origin}${location.pathname}?${params.toString()}` }
				onClick={ e => {
					e.preventDefault();
					onClick();
				} }
				{ ...props }
			>
				{ children && (
					<span>{ children }</span>
				) }
			</a>
			{ active && <Icon name="angle-right" /> }
		</span>
	);
}

export function Pagination({
	page = 1,
	max,
	next = 'Next',
	previous = 'Previous',
	pageNumbers = 3,
	onChange,
	...props
}) {
	page = Number( page );

	const numbers = [];
	const pre     = Math.max( 1, page - pageNumbers );
	const post    = Math.min( max, page + pageNumbers + 1 );
	const params  = new URLSearchParams( window.location.search );

	for ( let i = pre; i < page; i++ ) {
		numbers.push( i );
	}

	numbers.push( page );

	for ( let i = page + 1; i < post; i++ ) {
		numbers.push( i );
	}

	if ( ! numbers.includes( 1 ) ) {
		numbers.unshift( 1, '...' );
	}

	if ( ! numbers.includes( max ) ) {
		numbers.push( '...', max );
	}

	function getPageLink( num ) {
		params.set( 'page', num );

		return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
	}

	return (
		<nav className="pagination grants-db__pagination" aria-label="Pagination" {...props}>
			<ul className="pagination__items">
				<li className="pagination__item pagination__item--prev">
					<a
						href={ getPageLink( Math.max( 1, page - 1 ) ) }
						onClick={ (e) => {
							e.preventDefault();
							onChange( Math.max( 1, page - 1 ) );
						} }
						className="pagination__link link-override"
						disabled={ page === 1 }
					>
						<Icon name="angle-left" />
						<span className="a11y__">{ previous }</span>
					</a>
				</li>
				{ numbers && (
					<li className="pagination__item">
						<ul className="pagination__numbers">
							{ numbers.map( ( num ) => (
								<li
									key={ num }
									className={ classNames( {
										'pagination__number': true,
										'pagination__number--dots': num === '...',
										'pagination__number--current': num === page,
									} ) }
								>
									{ num === '...' || num === page ? (
										<span className={ classNames( {
											'pagination__link': true,
											'pagination__link--dots': num === '...',
											'pagination__link--current': num === page,
										} ) }>
											{ num }
										</span>
									) : (
										<a
											className="pagination__link link-override"
											onClick={ (e) => {
												e.preventDefault();
												onChange( num );
											} }
											href={ getPageLink( num ) }
										>
											{ num }
										</a>
									) }
								</li>
							) ) }
						</ul>
					</li>
				) }
				<li className="pagination__item pagination__item--next">
					<a
						className="pagination__link link-override"
						href={ getPageLink( Math.min( max, page + 1 ) ) }
						onClick={ (e) => {
							e.preventDefault();
							onChange( Math.min( max, page + 1 ) );
						} }
					>
						<span>{ next }</span>
						<Icon name="angle-right" />
					</a>
				</li>
			</ul>
		</nav>
	);
}

export function Facet({
	label = 'Facet Label',
	initOpen = false,
	children
}) {
	const [ open, setIsOpen ] = useState( initOpen );

	return (
		<div className={ classNames( 'grants-db__facet', { 'grants-db__facet--open': open } ) }>
			<button
				type="button"
				className="grants-db__facet-header"
				onClick={ () => setIsOpen( ! open ) }
			>
				<span className="grants-db__facet-header-text">{ label }</span>
				<i className="grants-db__facet-header-icon">{ open ? '–' : '+' }</i>
			</button>
			{ ( open && children ) ? (
				<div className="grants-db__facet-content">
					{ children }
				</div>
			) : null }
		</div>
	);
}

export function FilterList({
	value = [],
	options = [],
	onChange = () => {},
}) {
	if ( ! Array.isArray( value ) ) {
		value = [ value ].filter( v => v );
	}

	return (
		<ul className="grants-db__filters">
			{ options.map( option => {
				const active = value.includes( option.slug );

				return (
					<li key={ option.slug } className="grants-db__filter">
						<label className="grants-db__checkbox checkbox">
							<input
								type="checkbox"
								value={ option.value }
								checked={ active }
								onChange={ (e) => {
									if ( ! e.target.checked && active ) {
										onChange( [ ...value.filter( v => v !== option.slug ) ] );
									}

									if ( e.target.checked && ! active ) {
										onChange( [ ...value.concat( [ option.slug ] ) ] );
									}
								}}
							/>
							<span>{ option.name } ({option.count ?? '0'})</span>
						</label>
					</li>
				);
			} ) }
		</ul>
	)
}

export function CheckboxFilter({
	className,
	label,
	...props
}) {
	return (
		<li className={ classNames( 'grants-db__filter', className ) }>
			<label className="grants-db__filter grants-db__checkbox checkbox">
				<input
					type="checkbox"
					{...props}
				/>
				<span>{ label }</span>
			</label>
		</li>
	);
}

export function TermSearch({
	onChange,
	taxonomy,
	delay = 450,
	label,
	value = [],
	api = '/grant-api',
	...props
}) {
	const [ loading, setLoading ]           = useState( false );
	const [ search, setSearch ]             = useState( '' );
	const [ query, setQuery ]               = useState( '' );
	const [ store, setStore ]               = useState( {} );
	const [ queryResults, setQueryResults ] = useState( [] );

	// convert to array if a string is passed
	if ( ! Array.isArray( value ) ) {
		value = [ value ].filter( v => v );
	}

	// grab results from the data store
	const results = value.map( key => store[ key ] ? store[ key ] : null );

	function reduceStore( results ) {
		setStore( { ...store, ...results.reduce( ( carry, result ) => ( { ...carry, [ result.slug ]: result } ), {} ) } );
	}

	// update the store with preloaded data
	useEffect( () => {
		if ( props.results ) {
			reduceStore( props.results );
		}
	}, [] );

	// debounce typing in the search box
	useEffect( () => {
		let timer;

		if ( ! timer ) {
			timer = setTimeout( () => setQuery( search ), delay );
		}

		return () => {
			if ( timer ) {
				clearTimeout( timer );
				timer = null;
			}
		};
	}, [ search ] );

	// query results from the api when the debounced search has changed
	useEffect( () => {
		if ( query ) {
			setLoading( true );
			const params = new URLSearchParams( { subtype: taxonomy, search } );

			fetch( `${api}/term-search?${ params.toString() }` ).then((r) => {
				return r.json();
			}).then( data => {
				reduceStore( data );
				setQueryResults( data );
			}).finally(() => {
				setLoading( false );
			});
		} else {
			setQueryResults( [] );
		}
	}, [ query ] );

	return (
		<div className={ classNames( "grants-db__term-search", { 'grants-db__term-search--loading': loading } ) } style={ { opacity: loading ? '0.5' : '' } }>
			<label className="grants-db__term-search-label">
				{ label }<br />
				<input
					type="text"
					className="grants-db__term-search-input"
					placeholder=''
					value={ search }
					onChange={ e => setSearch( e.target.value ) }
				/>
			</label>

			{ ( results.length > 0 || queryResults.length > 0 ) && (
				<ol className="grants-db__term-search-suggestions grants-db__filters">
					{ results.map( result => result && (
						<CheckboxFilter
							key={ result.slug }
							className="grants-db__term-search-suggestion"
							name={ `${result}[]`}
							label={ result.name }
							value={ result.slug }
							checked
							onChange={ () => onChange( value.filter( val => val !== result.slug ) ) }
						/>
					) ) }
					{ queryResults && queryResults.filter( result => ! value.includes( result.slug ) ).map( result => result && (
						<CheckboxFilter
							key={ result.slug }
							className="grants-db__term-search-suggestion"
							name={ `${taxonomy}[]`}
							label={ result.name }
							value={ result.slug }
							onChange={ () => onChange( [ ...value, result.slug ] ) }
						/>
					) ) }
				</ol>
			) }
		</div>
	);
}

export function Icon( {
	name,
	height,
	width,
} ) {
	if ( 'angle-left' === name ) {
		return (
			<svg width={ width || 9 } height={ height || 16 } viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M0.1875 7.625C-0.046875 7.85938 -0.046875 8.1875 0.1875 8.42188L7.07812 15.3594C7.26562 15.5938 7.64062 15.5938 7.875 15.3594L8.76562 14.4219C9 14.1875 9 13.8594 8.76562 13.625L3.23438 8L8.76562 2.42188C9 2.1875 9 1.8125 8.76562 1.625L7.875 0.6875C7.64062 0.453125 7.26562 0.453125 7.07812 0.6875L0.1875 7.625Z" fill="currentColor" />
			</svg>
		);
	}

	if ( 'angle-right' === name ) {
		return (
			<svg xmlns="http://www.w3.org/2000/svg" width={ width || 10 } height={ height || 16 } viewBox="0 0 10 16">
				<path fill="currentColor" fillRule="evenodd" d="M2.01 15.784 10 8.127 2.01.47.568 1.854l6.545 6.273L.567 14.4l1.444 1.384Z" clipRule="evenodd"/>
			</svg>
		);
	}

	if ( 'clear' === name ) {
		return (
			<svg width={ width || 16 } height={ height || 16 } viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path fillRule="evenodd" clipRule="evenodd" d="M9.70625 8.00025L15.6473 2.05925C16.1182 1.58825 16.1182 0.82425 15.6473 0.35325C15.1763 -0.11775 14.4122 -0.11775 13.9412 0.35325L8.00025 6.29425L2.05925 0.35325C1.58825 -0.11775 0.82425 -0.11775 0.35325 0.35325C-0.11775 0.82425 -0.11775 1.58825 0.35325 2.05925L6.29425 8.00025L0.35325 13.9412C-0.11775 14.4122 -0.11775 15.1763 0.35325 15.6473C0.58825 15.8833 0.89725 16.0002 1.20625 16.0002C1.51525 16.0002 1.82325 15.8822 2.05925 15.6473L8.00025 9.70625L13.9412 15.6473C14.1772 15.8833 14.4852 16.0002 14.7942 16.0002C15.1032 16.0002 15.4113 15.8822 15.6473 15.6473C16.1182 15.1763 16.1182 14.4122 15.6473 13.9412L9.70625 8.00025Z" fill="currentColor"/>
			</svg>
		)
	}

	if ( 'search' === name ) {
		return (
			<svg width={ width || 21 } height={ height || 22 } fill="none">
				<circle cx="9.434" cy="9.327" r="8.466" stroke="#0F0F0F" strokeWidth="1.722" />
				<path stroke="#000" strokeWidth="1.722" d="m14.884 15.534 5.381 5.381" />
			</svg>
		);
	}

	if ( 'download' === name ) {
		return (
			<svg width={ width || 12 } height={ height || 14 } viewBox="0 0 12 14">
				<path d="M11.25 14C11.6562 14 12 13.6875 12 13.25V12.5C12 12.0938 11.6562 11.75 11.25 11.75H0.75C0.3125 11.75 0 12.0938 0 12.5V13.25C0 13.6875 0.3125 14 0.75 14H11.25ZM9.15625 5.25L7.125 7.3125V0.75C7.125 0.34375 6.78125 0 6.375 0H5.625C5.1875 0 4.875 0.34375 4.875 0.75V7.3125L2.8125 5.25C2.53125 4.96875 2.03125 4.96875 1.75 5.25L1.21875 5.78125C0.9375 6.0625 0.9375 6.5625 1.21875 6.84375L5.46875 11.0625C5.75 11.375 6.21875 11.375 6.5 11.0625L10.75 6.84375C11.0312 6.53125 11.0312 6.0625 10.75 5.78125L10.2188 5.25C9.9375 4.96875 9.4375 4.96875 9.15625 5.25Z" fill="#000" />
			</svg>
		)
	}

	if ( 'menu' === name ) {
		return (
			<svg width={ width || 18 } height={ height || 14 } viewBox="0 0 18 14" fill="none">
				<path d="M18 13.4473H0V11.3121H18V13.4473ZM18 8.10935H0V5.97417H18V8.10935ZM18 2.77141H0V0.63623H18V2.77141Z" fill="currentColor" />
			</svg>
		)
	}

	return null;
}

export function RemoveFilterButton({
	label,
	text,
	className,
	...props
}) {
	return (
		<div className="wp-block-button is-style-button-small">
			<button
				type="button"
				className={ classNames( 'grants-db__active-filter', 'wp-block-button__link', className ) }
				{ ...props }
			>
				<span className="grants-db__active-filter-label">
					{ label }

					<Icon name="clear" />
				</span>
				{ text }
			</button>
		</div>
	);
}

export function TotalStatement({
	totalGrants = 0,
	totalGrantees = 0,
	totalAmount = 0,
	minYear = 0,
	maxYear = 0,
	...props
}) {
	{/* <strong className="h3">{ totalGrants.toLocaleString() } { totalGrants === 1 ? 'result' : 'results' } to { totalGrantees.toLocaleString() } { totalGrantees === 1 ? 'grantee' : 'grantees' }</strong> totaling { USDollar.format( totalAmount ) } between { minYear } and { maxYear } */}
	const totalGrantsStr   = `${totalGrants.toLocaleString()} ${totalGrants === 1 ? 'result' : 'results'}`;
	const totalGranteesStr = totalGrantees > 0 ? `to ${ totalGrantees.toLocaleString() } ${ totalGrantees === 1 ? 'grantee' : 'grantees' }` : '';
	const betweenStr       = minYear && maxYear ? `between ${ minYear } and ${ maxYear }` : '';

	return (
		<div className="grants-db__totals" { ...props }>
			<strong className="h3">{ totalGrantsStr } { totalGranteesStr }</strong> { betweenStr }
		</div>
	);
}
